// material-ui
import { styled } from '@mui/material/styles';
import { Box, Stack } from '@mui/material';

// third-party
import { useDropzone } from 'react-dropzone';

// project import
import RejectionFiles from './RejectionFiles';
import PlaceholderContent from './PlaceholderContent';
import FilesPreview from './FilesPreview';

// types
import { DropzoneType, UploadProps } from 'types/dropzone';

const DropzoneWrapper = styled('div')(({ theme }) => ({
    outline: 'none',
    padding: theme.spacing(4, 1),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    border: `1px dashed ${theme.palette.secondary.main}`,
    '&:hover': { opacity: 0.72, cursor: 'pointer' },
}));

// ==============================|| UPLOAD - MULTIPLE FILE ||============================== //

const FileUpload = ({
    error,
    type,
    file,
    name,
    sx,
    accept,
    maxSize,
    validator,
    setFieldValue,
    setFieldTouched,
}: UploadProps) => {
    const { getRootProps, getInputProps, isDragActive, isDragReject, fileRejections } = useDropzone({
        accept,
        maxSize,
        validator,
        multiple: false,
        onDrop: (acceptedFiles) => {
            if (acceptedFiles && acceptedFiles[0]) {
                setFieldValue(name, acceptedFiles[0]);
                setFieldTouched(name, true, false);
            } else {
                setFieldValue(name, null);
            }
        },
    });

    const onRemove = () => {
        setFieldValue(name, null);
    };

    return (
        <Box
            sx={{
                width: '100%',
                ...(type === DropzoneType.standard && { width: 'auto', display: 'flex' }),
                ...sx,
            }}
        >
            <Stack {...(type === DropzoneType.standard && { alignItems: 'center' })}>
                <DropzoneWrapper
                    {...getRootProps()}
                    sx={{
                        ...(type === DropzoneType.standard && {
                            p: 0,
                            m: 1,
                            width: 64,
                            height: 64,
                        }),
                        ...(isDragActive && { opacity: 0.72 }),
                        ...((isDragReject || error) && {
                            color: 'error.main',
                            borderColor: 'error.light',
                            bgcolor: 'error.lighter',
                        }),
                    }}
                >
                    <input {...getInputProps()} name={name} />
                    <PlaceholderContent type={type} />
                </DropzoneWrapper>
            </Stack>
            {fileRejections.length > 0 && <RejectionFiles fileRejections={fileRejections} />}
            {file && <FilesPreview files={[file]} showList={false} onRemove={onRemove} type={type} />}
        </Box>
    );
};

export default FileUpload;
